'use client';

import { useMemo, type SyntheticEvent } from 'react';
import { Box, token, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../../components/badge';
import { GatheredArtifactsTooltipContent } from '../../application-credentials/GatheredArtifactsPanel';
import { Spinner } from '../../../components/spinner';
import { Popover } from '../../../components/popover';
import { InformationCircleIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { CredentialsApiDto } from '@terra/shared-contracts-applications';
import type { CachedCredentialArtifactSummary } from '@terra/shared-contracts-authenticator';

type ArtifactWithValue = CachedCredentialArtifactSummary & {
  value?: string;
};
type VerificationBadgeProps = {
  credential: CredentialsApiDto['credentials'][number];
  isTesting?: boolean;
  artifacts?: ArtifactWithValue[];
};

const stopCardToggle = (event: SyntheticEvent) => {
  event.stopPropagation();
};

export const VerificationBadge = ({
  credential,
  isTesting,
  artifacts = [],
}: VerificationBadgeProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const status = credential.verification?.status;
  const isVerifying = status === 'VERIFYING' || isTesting;
  const isVerified = status === 'VERIFIED';
  const text = useMemo(() => {
    if (isVerifying) {
      return t('verifying');
    }

    if (isVerified) {
      return t('verified');
    }

    if (status === 'FAILED') {
      return t('verificationFailed');
    }

    return t('notVerified');
  }, [status, isVerifying, isVerified, t]);
  const bg = useMemo(() => {
    if (isVerifying) {
      return token('colors.bg.accent.light');
    }

    if (isVerified) {
      return token('colors.bg.neutral.success');
    }

    if (status === 'FAILED') {
      return token('colors.bg.neutral.error');
    }

    return undefined;
  }, [status, isVerifying, isVerified]);
  const hasArtifacts = isVerified && artifacts.length > 0;
  const badge = (
    <Badge
      size="sm"
      bg={bg}
      variant="fill"
      color={isVerifying ? 'fg.accent.muted' : undefined}
      leadingContent={
        isVerifying ? (
          <Spinner size="sm" borderColor="bg.accent.default" />
        ) : undefined
      }
      icon={hasArtifacts ? <InformationCircleIcon size={10} /> : undefined}
    >
      {text}
    </Badge>
  );

  if (!hasArtifacts) {
    return (
      <Box display="inline-flex" onClick={stopCardToggle}>
        {badge}
      </Box>
    );
  }

  return (
    <Box display="inline-flex" onClick={stopCardToggle}>
      <Popover.Root
        positioning={{ strategy: 'fixed', placement: 'bottom-start' }}
        autoFocus={false}
      >
        <Popover.Trigger asChild>
          <Box
            as="button"
            cursor="pointer"
            display="inline-flex"
            aria-label={text}
          >
            {badge}
          </Box>
        </Popover.Trigger>
        <Popover.Positioner>
          <Popover.Content
            maxW="420px"
            p={3}
            onClick={stopCardToggle}
            onPointerDown={stopCardToggle}
          >
            <VStack gap={2} alignItems="stretch" w="full">
              <GatheredArtifactsTooltipContent artifacts={artifacts} />
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </Box>
  );
};
